import {
  EarningsInsightFilterResponse,
  earningsInsights,
  InsightFilterRequestSchema,
  insights,
} from '@api';
import { getTimeTillEndOfDayMs } from '@helpers';
import { useQuery } from '@tanstack/react-query';
import { postEntity } from '@terminal/services/entities/entity-service';
import {
  fetchSnakeByName,
  revampFetchSnakeByName,
} from '@terminal/services/overview-widget/overview-widget-service';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { ApiFetchResponse, apiFetch } from '@terminal/utils/api-fetch/apiFetch';
import { InsightWidgetData } from '@terminal/views/alfred/quick-analysis/use-chat/assistant-response.type';
import { subWeeks } from 'date-fns';
import { format, toZonedTime } from 'date-fns-tz';

import { formatCumulativeReturnSnake } from './utils/insight-utils';

const INSIGHTS_LIMIT_IN_WEEKS = 1;

export type GetFilteredInsightsProps = Omit<
  InsightFilterRequestSchema,
  'from_date' | 'to_date'
> & {
  from_date?: Date;
  to_date?: Date;
};

export const getFilteredInsights = ({
  topic,
  asset_cls,
  class: cls,
  eco,
  entity,
  event,
  horizon,
  last_value__lte,
  market_cap_usd_bn__gte,
  market_cap_usd_bn__lt,
  median_return__gte,
  median_return__lt,
  sector,
  stars,
  sub_class,
  from_date = subWeeks(new Date(), INSIGHTS_LIMIT_IN_WEEKS),
  to_date,
  page,
  limit,
  tags_only,
}: GetFilteredInsightsProps) => {
  const payload: InsightFilterRequestSchema = {
    topic,
    asset_cls,
    class: cls,
    eco,
    entity,
    event,
    horizon,
    last_value__lte,
    market_cap_usd_bn__gte,
    market_cap_usd_bn__lt,
    median_return__gte,
    median_return__lt,
    sector,
    stars,
    sub_class,
    from_date: from_date.toISOString(),
    to_date: to_date?.toISOString(),
    page,
    limit,
    tags_only,
  };

  Object.keys(payload).forEach(
    key =>
      payload[key as keyof InsightFilterRequestSchema] === undefined &&
      delete payload[key as keyof InsightFilterRequestSchema]
  );

  return apiFetch(insights.insightFilter.path, {
    schema: insights.insightFilter.schema,
    method: 'post',
    body: payload,
  });
};

export const getInsight = (id: string) => {
  return apiFetch(insights.insight.path(id), {
    schema: insights.insight.schema,
  });
};

export const getInsightPredictions = (id: string) => {
  return apiFetch(insights.predictions.path(id), {
    schema: insights.predictions.schema,
  });
};

export const getInsightWidgetData = async (
  insightId: string
): Promise<ApiFetchResponse<InsightWidgetData>> => {
  const insightRes = await getInsight(insightId);
  const isCalculatorNormalizedSnakesFlagEnabled = useFlags
    .getState()
    .getFeatureFlagState('DATA-1237-api_ca_v3');

  if (insightRes.error) {
    return insightRes;
  }

  const insight = insightRes.data;

  const entityRes = await postEntity(insight.metadata.entity);

  if (entityRes.error) {
    return entityRes;
  }

  const entity = entityRes.data;

  const startDate = format(
    toZonedTime(
      new Date(insight.metadata.last_value_date ?? insight.created_at),
      'UTC'
    ),
    'yyyyMMdd'
  );

  const fetchFn = isCalculatorNormalizedSnakesFlagEnabled
    ? revampFetchSnakeByName
    : fetchSnakeByName;
  const snake = await fetchFn(
    `${entity.tag}.${entity.primary_method}.cumulative_returns(date=${startDate})`
  );

  let snakeTs = [] as { index: number; value: number }[];

  if (!snake.error) {
    snakeTs = formatCumulativeReturnSnake(
      snake.data.result.data,
      insight.metadata.last_value_date ?? insight.created_at
    );
  }

  return {
    validationError: false,
    data: {
      insight,
      entity,
      snakeTs,
    },
  };
};

export const getEarningsInsightData = (earningsId: string) => {
  const { path, schema } = earningsInsights.postEarningsInsightFilter;
  return apiFetch<EarningsInsightFilterResponse>(path, {
    method: 'post',
    body: { earnings_ids: [earningsId] },
    schema,
  });
};

const QUERY_KEY_INSIGHT_WIDGET_DATA = 'QUERY_KEY_INSIGHT_WIDGET_DATA';
const QUERY_KEY_INSIGHT_PREDICTIONS = 'QUERY_KEY_INSIGHT_PREDICTIONS';

export const useGetInsightWidgetData = (insightId: string) =>
  useQuery({
    queryKey: [QUERY_KEY_INSIGHT_WIDGET_DATA, insightId],
    queryFn: () => getInsightWidgetData(insightId),
    enabled: !!insightId,
    staleTime: getTimeTillEndOfDayMs(),
  });

export const useGetInsightPredictions = (insightId: string) =>
  useQuery({
    queryKey: [QUERY_KEY_INSIGHT_PREDICTIONS, insightId],
    queryFn: () => getInsightPredictions(insightId),
    enabled: !!insightId,
    staleTime: getTimeTillEndOfDayMs(),
  });
